// Per-conversation theme overrides. Each user can pick a wallpaper +
// font-size override for a specific chat that only applies to their own
// view (other members see their own choices). Stored in localStorage
// because this is a per-device personalization, not synced across the
// user's devices and never broadcast to anyone else.
//
// Falls back to the global ChatPrefs (utils/chatPrefs.ts) when no
// override is set for the conversation. Schema:
//   { [conversationId]: { wallpaper?: Wallpaper, fontSize?: FontSize } }
// Empty objects (or fields set to null) mean "no override on this field"
// — the global default takes over.

import { useEffect, useState } from 'react';
import type { FontSize, Wallpaper } from './chatPrefs';

export interface ConvThemeOverride {
  wallpaper?: Wallpaper;
  fontSize?: FontSize;
}

type OverrideMap = Record<string, ConvThemeOverride>;

const KEY = 'chat:conv-theme-overrides';
const EVENT = 'chat:conv-theme-changed';

function load(): OverrideMap {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as OverrideMap;
    if (typeof parsed !== 'object' || parsed === null) return {};
    return parsed;
  } catch {
    return {};
  }
}

function save(map: OverrideMap): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(map));
  } catch {
    /* localStorage full or disabled — best-effort is fine */
  }
}

export function getThemeOverride(conversationId: string): ConvThemeOverride {
  return load()[conversationId] ?? {};
}

// Patch one or more fields of the conversation's override. Setting a
// field to undefined OR null clears it; passing an empty object is a
// no-op. Fires the chat:conv-theme-changed event so the chat panel
// re-renders with the new style.
//
// The type is wider than ConvThemeOverride itself so the picker UI
// can pass null to mean "clear this field" (UI uses null for the
// "Use default" option) without forcing every call site to special-
// case it. Internally we treat null and undefined identically.
export function setThemeOverride(
  conversationId: string,
  patch: { wallpaper?: Wallpaper | null; fontSize?: FontSize | null },
): void {
  const map = load();
  const current = map[conversationId] ?? {};
  const next: ConvThemeOverride = { ...current };
  for (const [k, v] of Object.entries(patch) as Array<[keyof ConvThemeOverride, unknown]>) {
    if (v === undefined || v === null) {
      delete (next as Record<string, unknown>)[k];
    } else {
      (next as Record<string, unknown>)[k] = v;
    }
  }
  if (Object.keys(next).length === 0) {
    delete map[conversationId];
  } else {
    map[conversationId] = next;
  }
  save(map);
  window.dispatchEvent(
    new CustomEvent<{ conversationId: string }>(EVENT, {
      detail: { conversationId },
    }),
  );
}

// Convenience: drop the entire override for a conversation, reverting to
// the global ChatPrefs default.
export function clearThemeOverride(conversationId: string): void {
  const map = load();
  if (!(conversationId in map)) return;
  delete map[conversationId];
  save(map);
  window.dispatchEvent(
    new CustomEvent<{ conversationId: string }>(EVENT, {
      detail: { conversationId },
    }),
  );
}

// Hook: subscribe to a single conversation's theme override. Returns the
// current override object (empty if none). Re-renders on local changes
// AND cross-tab storage events so multiple open tabs of the same chat
// stay aligned.
export function useThemeOverride(conversationId: string): ConvThemeOverride {
  const [override, setOverride] = useState<ConvThemeOverride>(() =>
    getThemeOverride(conversationId),
  );
  useEffect(() => {
    setOverride(getThemeOverride(conversationId));
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<{ conversationId: string }>).detail;
      if (detail?.conversationId !== conversationId) return;
      setOverride(getThemeOverride(conversationId));
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === KEY) setOverride(getThemeOverride(conversationId));
    };
    window.addEventListener(EVENT, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, [conversationId]);
  return override;
}

// Map a Wallpaper value to its CSS background-image expression. Centralized
// here so the per-conv override can apply it via inline style without
// re-parsing the CSS rules. The 'default' case yields no override —
// the global root's data-wallpaper still applies.
const WALLPAPER_IMAGES: Record<Wallpaper, string | null> = {
  default: null,
  indigo: 'linear-gradient(155deg, #18162a 0%, #1d1838 50%, #2c2554 100%)',
  forest: 'linear-gradient(155deg, #0d1f17 0%, #14281c 50%, #1d3829 100%)',
  sand: 'linear-gradient(155deg, #2a221a 0%, #342a20 50%, #3e3225 100%)',
  mono: 'linear-gradient(155deg, #0a0a0a 0%, #1a1a1a 50%, #2a2a2a 100%)',
  aurora:
    'radial-gradient(at 20% 20%, rgba(99, 102, 241, 0.25), transparent 55%), radial-gradient(at 80% 80%, rgba(16, 185, 129, 0.18), transparent 55%), linear-gradient(155deg, #0a0a14 0%, #14122a 50%, #0e1a22 100%)',
};

const FONT_SIZE_VALUES: Record<FontSize, string> = {
  small: '13px',
  medium: '14px',
  large: '16px',
};

// Build the inline style object the ChatPanel applies to its message-area
// container. Returns CSSProperties suitable to spread; only includes the
// vars that are actually overridden so the global root's defaults bleed
// through for unset fields.
export function overrideStyle(override: ConvThemeOverride): React.CSSProperties {
  const style: Record<string, string> = {};
  if (override.wallpaper) {
    const img = WALLPAPER_IMAGES[override.wallpaper];
    if (img) style['--chat-wallpaper-image'] = img;
    else style['--chat-wallpaper-image'] = 'none';
  }
  if (override.fontSize) {
    style['--chat-message-size'] = FONT_SIZE_VALUES[override.fontSize];
  }
  return style as React.CSSProperties;
}
